/**
 * Used in conjunction with class methods, making sure that they'll be bound to
 * the context of their class instance.
 */
export const autobind = (context?: unknown) => {
    return (target: {}, propertyKey: string, descriptor: PropertyDescriptor) => {
        // Create a cache reference to the original method (we're going to
        // overwrite it with this decorator, but we still need to be able to
        // trigger it!)
        let callback = <(...args: unknown[]) => unknown> descriptor.value;

        return {
            configurable: true,
            enumerable: descriptor.enumerable,

            // Create a getter, which is where we're going to properly bind the
            // method to this context (that way we'll be able to reliably read
            // "this" and bind our callback)
            get(): (...args: any[]) => any { // tslint:disable-line no-any
                // Create a bound version of the callback function
                // tslint:disable-next-line no-invalid-this
                const boundCallback = <(...args: unknown[]) => unknown> callback.bind(context || this);

                // Re-configure the getter after having read the property the
                // first them (as we've now already had access to "this" and
                // created a bound callback)
                // tslint:disable-next-line no-invalid-this
                Object.defineProperty(this, propertyKey, {
                    configurable: false,
                    enumerable: descriptor.enumerable,

                    // Return the bound callback for future reference
                    // tslint:disable-next-line no-reserved-keywords
                    get(): unknown {
                        return boundCallback;
                    },
                });

                // Return the bound callback immediately
                return boundCallback;
            },

            // Create a setter to allow overwriting the original callback, juust
            // in case someone wants to do so...
            // tslint:disable-next-line no-reserved-keywords no-any
            set(value: (...args: any[]) => unknown): void {
                callback = value;
            },
        };
    };
};
